import { Component } from '@theme/component';

/**
 * @typedef {{ id: string | number, name: string, qty: number }} StoreLocation
 */

/**
 * @typedef {{ title: string, address: string }} StoreInfo
 */

/**
 * @typedef {Object} StoreAvailabilityRefs
 * @property {HTMLElement} [results]
 * @property {HTMLElement} [empty]
 * @property {HTMLScriptElement} [storesData]
 */

/**
 * Multi-size store availability: reads per-variant stock data embedded
 * directly on each size checkbox (data-locations, sourced from the
 * custom.stocks_por_loja variant metafield) and merges the checked sizes'
 * locations client-side. No network requests involved.
 *
 * The metafield's location `name` is used only to match against the
 * stores metaobject (via a normalized key, e.g. city_code). The display
 * title and address shown to the user always come from the metaobject.
 *
 * @extends {Component<StoreAvailabilityRefs>}
 */
class SkreyStoreAvailability extends Component {
  requiredRefs = ['results'];

  /** @type {Map<string, StoreInfo>} */
  #storesByKey = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#storesByKey = this.#parseStores();
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') return;
    this.#renderMergedLocations();
  };

  #renderMergedLocations() {
    const results = this.refs.results;
    if (!(results instanceof HTMLElement)) return;

    const checkedInputs = this.#getSizeInputs().filter((input) => input.checked);
    const empty = this.refs.empty;

    if (checkedInputs.length === 0) {
      results.replaceChildren();
      if (empty instanceof HTMLElement) empty.hidden = true;
      return;
    }

    /** @type {Map<string, { id: string | number, name: string, sizes: string[] }>} */
    const merged = new Map();

    for (const input of checkedInputs) {
      const sizeName = input.dataset.sizeName || '';
      const locations = this.#parseLocations(input.dataset.locations);

      for (const location of locations) {
        if (!location || Number(location.qty) <= 0) continue;

        const key = String(location.id ?? location.name);
        const existing = merged.get(key);

        if (existing) {
          if (!existing.sizes.includes(sizeName)) existing.sizes.push(sizeName);
        } else {
          merged.set(key, { id: location.id, name: location.name, sizes: [sizeName] });
        }
      }
    }

    results.replaceChildren();

    if (merged.size === 0) {
      if (empty instanceof HTMLElement) empty.hidden = false;
      return;
    }

    if (empty instanceof HTMLElement) empty.hidden = true;

    const fragment = document.createDocumentFragment();

    for (const store of [...merged.values()].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
      })
    )) {
      const item = document.createElement('li');
      item.className = 'skrey-store-availability__location';

      const matchedStore = this.#storesByKey.get(this.#normalizeName(store.name));

      const name = document.createElement('p');
      name.className = 'skrey-store-availability__location-name';
      // Prefer the metaobject's display title; fall back to the raw
      // metafield name so the store still shows up if no match is found.
      name.textContent = matchedStore?.title || store.name || '';
      item.append(name);

      if (matchedStore?.address) {
        const address = document.createElement('p');
        address.className = 'skrey-store-availability__location-address';
        address.textContent = matchedStore.address;
        item.append(address);
      }

      const sizes = document.createElement('p');
      sizes.className = 'skrey-store-availability__location-sizes';

      const sizesLabel = this.dataset.sizesLabel || 'Tamanhos:';
      sizes.textContent = `${sizesLabel} ${store.sizes.join(', ')}`;
      item.append(sizes);

      fragment.append(item);
    }

    results.append(fragment);
  }

  /**
   * @param {string | undefined} raw
   * @returns {StoreLocation[]}
   */
  #parseLocations(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[skrey-store-availability] Invalid JSON in data-locations', error);
      return [];
    }
  }

  /**
   * @returns {HTMLInputElement[]}
   */
  #getSizeInputs() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-variant-id]'));
  }

  /**
   * Parses the embedded stores metaobject data into a lookup map keyed by
   * normalized store code (e.g. city_code), each entry holding the
   * display title and address to present.
   * @returns {Map<string, StoreInfo>}
   */
  #parseStores() {
    const ref = this.refs.storesData;
    const scriptEl = Array.isArray(ref) ? ref[0] : ref;
    const raw = scriptEl?.textContent;
    if (!raw) return new Map();

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Map();

      return new Map(
        parsed
          .filter((store) => store && store.key)
          .map((store) => [
            this.#normalizeName(store.key),
            { title: store.title || '', address: store.address || '' },
          ])
      );
    } catch (error) {
      console.warn('[skrey-store-availability] Invalid JSON in storesData', error);
      return new Map();
    }
  }

  /**
   * @param {string | undefined | null} name
   * @returns {string}
   */
  #normalizeName(name) {
    return String(name || '').trim().toLowerCase();
  }
}

if (!customElements.get('skrey-store-availability')) {
  customElements.define('skrey-store-availability', SkreyStoreAvailability);
}